import { useEffect, useMemo, useState } from 'react';
import qs from 'qs';
import * as _ from 'lodash';
import { useAsyncStatus } from '../../../../../hooks';
import * as API from '../../../../../redux/api';
import { isDeclaredLicense } from '../../../../../utils';
import type { FailedFetchResponse } from '../../../../../helpers/errors';
import type { Match, RevisionLicense } from '../../../../../../typings/api';

//  We only store a subset of Match
export type RevisionFileMatch = Pick<Match, 'id' | 'path' | 'sources' > & { revisionLicense: RevisionLicense };

//  We only care about a subset of the returned data, so we "_.pick" it here.
const toRevisionFileMatch = ({ id, path, sources, RevisionLicense: revisionLicense }: Match): RevisionFileMatch => ({
  id,
  path,
  sources,
  revisionLicense,
});

/**
 * "Sanitizes" the incoming matches by removing the declared license,
 * then picking just a subset of the fields which are needed for the FileViewer.
 */
const sanitizeMatches = (matches: Match[] = []): RevisionFileMatch[] => matches.map(toRevisionFileMatch);

/**
 * Given a revisionId and (optionally) a licenseId, this will fetch a set
 * of Matches for that Revision. Manages the status of the network request,
 * as well as storing the matches themselves.
 *
 * Note that `revisionId` may be undefined in cases where the issue details
 * (or other data source) has not loaded yet. In these cases, there is no
 * side-effect/we bail out of doing the request. `licenseId` is truly optional though,
 * and it is possible to request the list of matches without this.
 */
function useFileMatchesForRevision(revisionId?: string, licenseId?: string) {
  //  TODO: Use the useAsyncValue hook when this lands: https://github.com/fossas/FOSSA/pull/10963
  const { status, onPending, onSuccess, onError } = useAsyncStatus();
  const [matches, setMatches] = useState<RevisionFileMatch[]>([]);
  const [isCliLicenseScanWithMatchData, setIsCliLicenseScanWithMatchData] = useState<boolean>(false);

  //  This will fetch new matches every time revisionId or licenseId are changed.
  useEffect(() => {
    async function getMatches() {
      try {
        const url = `/api/matches?${qs.stringify({
          revisionId,
          licenseId,
        })}`;
        onPending();
        const { data } = await API.get<{ matches: Match[]; cliLicenseScanWithMatchData: boolean }>(url);
        setIsCliLicenseScanWithMatchData(data.cliLicenseScanWithMatchData);
        if (data?.matches && Array.isArray(data.matches)) {
          setMatches(sanitizeMatches(data.matches));
          onSuccess();
        } else {
          onError('File matches could not be fetched!');
        }
      } catch (error) {
        onError(error as FailedFetchResponse);
      }
    }

    //  Guard against falsey `revisionId`, since it'd be a BadRequestError
    if (revisionId) {
      getMatches();
    }
  }, [revisionId, licenseId, onPending, onSuccess, onError]);

  return {
    status,
    matches,
    isCliLicenseScanWithMatchData,
    declaredLicenses: useMemo(
      () =>
        // Prevent duplicate matches with the same licenseId
        _.uniqBy(
          matches.filter(isDeclaredLicense).map((match) => match.revisionLicense),
          (license) => license.licenseId,
        ),
      [matches],
    ),
    isDeclaredLicense: useMemo(() => matches.some(isDeclaredLicense), [matches]),
  };
}

export default useFileMatchesForRevision;
